/* ==========================================================================
   Kenza Pro — Motion Core
   Single IntersectionObserver-driven reveal engine for [data-kp-reveal].
   Also provides global utilities: kpHaptic, kpIsMobile, kpReducedMotion.
   ========================================================================== */

(() => {
  if (window.__kpMotionBooted) return;
  window.__kpMotionBooted = true;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MOBILE_BP = 749;
  const isMobile = () => window.matchMedia(`(max-width: ${MOBILE_BP}px)`).matches;

  // Effects that are too heavy / janky on small screens — fall back to fade-up.
  const MOBILE_FALLBACK = new Set(['split-words', 'split-lines', 'mask-up']);

  // ---------- Public utilities (window.KP) ----------
  window.KP = window.KP || {};
  window.KP.reducedMotion = REDUCED;
  window.KP.isMobile = isMobile;

  // Haptic feedback — no-ops on iOS (Apple disabled vibration in Safari) but
  // never throws. Magnetic CTA + add-to-cart hook into this.
  window.KP.haptic = (type = 'light') => {
    if (!('vibrate' in navigator)) return;
    const map = { light: 5, medium: 10, heavy: 20 };
    const ms = map[type] ?? 5;
    try { navigator.vibrate(ms); } catch (_) { /* swallow */ }
  };

  const revealsDisabled = document.documentElement.dataset.kpReveals === 'off';

  if (REDUCED || revealsDisabled) {
    // Reveal everything instantly + skip observer setup.
    const flush = () => {
      document.querySelectorAll('[data-kp-reveal]:not(.kp-is-revealed)')
        .forEach(el => el.classList.add('kp-is-revealed'));
      // Legacy reveal attribute (from old kenza-pro-reveal snippet)
      document.querySelectorAll('[data-reveal]:not(.is-revealed)')
        .forEach(el => el.classList.add('is-revealed'));
    };
    flush();
    document.addEventListener('shopify:section:load', flush);
    return;
  }

  // ---------- Split helpers ----------
  const splitWords = (el) => {
    if (el.dataset.kpSplit === 'done') return;
    // Preserve <em>/<i> styling — walk children and wrap text nodes only.
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    let n;
    while ((n = walker.nextNode())) textNodes.push(n);

    textNodes.forEach(node => {
      const parent = node.parentNode;
      const words = node.textContent.split(/(\s+)/);
      const frag = document.createDocumentFragment();
      words.forEach(w => {
        if (w.trim() === '') {
          frag.appendChild(document.createTextNode(w));
        } else {
          const span = document.createElement('span');
          span.className = 'kp-word';
          span.textContent = w;
          frag.appendChild(span);
        }
      });
      parent.replaceChild(frag, node);
    });
    el.dataset.kpSplit = 'done';
  };

  const splitLines = (el) => {
    // Approximate "lines" by splitting on <br> and on natural wraps.
    // For headlines we use word-wrapper with overflow:hidden on parent — cheaper than measuring.
    if (el.dataset.kpSplit === 'done') return;
    splitWords(el);
    el.classList.add('kp-split-lines');
  };

  // ---------- Apply effect ----------
  const applyEffect = (el) => {
    let effect = el.dataset.kpReveal || 'fade-up';
    if (isMobile() && MOBILE_FALLBACK.has(effect)) effect = 'fade-up';

    const delay    = parseInt(el.dataset.kpDelay,    10) || 0;
    const duration = parseInt(el.dataset.kpDuration, 10) || 0;
    if (delay)    el.style.setProperty('--kp-rv-delay',    `${delay}ms`);
    if (duration) el.style.setProperty('--kp-rv-duration', `${duration}ms`);

    if (effect === 'split-words' || effect === 'split-lines') {
      if (effect === 'split-lines') splitLines(el); else splitWords(el);
      const stagger = parseInt(el.dataset.kpStagger, 10) || 60;
      el.querySelectorAll('.kp-word').forEach((w, i) => {
        w.style.setProperty('--kp-rv-delay', `${delay + i * stagger}ms`);
      });
    }

    el.classList.add(`kp-reveal--${effect}`);
    requestAnimationFrame(() => el.classList.add('kp-is-revealed'));
  };

  // ---------- Single observer ----------
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting) {
        applyEffect(e.target);
        io.unobserve(e.target);
      }
    }
  }, { rootMargin: '0px 0px -6% 0px', threshold: 0.05 });

  const observeAll = () => {
    document.querySelectorAll('[data-kp-reveal]:not(.kp-is-revealed)').forEach(el => io.observe(el));
    // Legacy compat — bridge old [data-reveal] system to the new classlist.
    document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach(el => {
      const o2 = new IntersectionObserver((entries, obs) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            obs.unobserve(e.target);
          }
        }
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
      o2.observe(el);
    });
  };

  // ---------- Counter (used by hero stats) ----------
  const animateCounter = (el) => {
    if (el.dataset.kpCounterDone === '1') return;
    el.dataset.kpCounterDone = '1';
    const raw = (el.dataset.kpCounter ?? el.textContent).toString().trim();
    // Extract leading number, keep suffix (e.g., "8000+" → 8000, "+")
    const match = raw.match(/^([0-9.,]+)(.*)$/);
    if (!match) return;
    const target = parseFloat(match[1].replace(/[.,]/g, ''));
    const suffix = match[2] || '';
    if (isNaN(target)) return;
    const dur = Math.min(2000, 600 + target * 0.05);
    const start = performance.now();
    const ease = t => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const v = Math.round(target * ease(t));
      el.textContent = v.toLocaleString('nl-NL') + suffix;
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const counterIO = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting) {
        animateCounter(e.target);
        counterIO.unobserve(e.target);
      }
    }
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.3 });

  const observeCounters = () => {
    document.querySelectorAll('[data-kp-counter]:not([data-kp-counter-done="1"])')
      .forEach(el => counterIO.observe(el));
  };

  // ---------- Boot ----------
  const boot = () => {
    observeAll();
    observeCounters();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-scan on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', boot);
})();
